import React, { useState, useEffect } from 'react';
import { Form, Card, Alert, Spinner } from 'react-bootstrap';
import { FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';
import axios from 'axios';
import config from '../../config';

const CompanyAddressSelect = ({
  value = {},
  onChange,
  required = false,
  disabled = false,
  className = '',
  showCompanyInfo = true,
  allowCustomAddress = true
}) => {
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useCompanyAddress, setUseCompanyAddress] = useState(true);
  const [customAddress, setCustomAddress] = useState({
    street: value.street || '',
    city: value.city || '',
    state: value.state || '',
    pincode: value.pincode || ''
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompanySettings(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching company settings:', error);
      setError('Failed to load company address');
    } finally {
      setLoading(false);
    }
  };

  const handleUseCompanyAddress = (useCompany) => {
    setUseCompanyAddress(useCompany);
    if (useCompany && onChange) {
      // Format: Company address format (street, city, state - pincode)
      const companyAddress = companySettings?.address;
      const formattedAddress = companyAddress && companyAddress.street
        ? `${companyAddress.street}, ${companyAddress.city}, ${companyAddress.state} - ${companyAddress.pincode}`
        : '';
      onChange(formattedAddress);
    } else if (!useCompany && onChange) {
      // Return current custom address as formatted string
      if (customAddress.street) {
        if (onChange) {
          onChange(`${customAddress.street}, ${customAddress.city}, ${customAddress.state} - ${customAddress.pincode}`);
        }
      }
    }
  };



  if (loading) {
    return (
      <div className={className}>
        <div className="d-flex align-items-center mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading company address...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <Alert variant="warning" className="py-2">
          <small>{error}</small>
        </Alert>
      </div>
    );
  }

  const companyAddress = companySettings?.address || {};

  return (
    <div className={className}>
      {showCompanyInfo && companyAddress.street && (
        <Card className="mb-3 border-primary">
          <Card.Header className="bg-primary text-white py-2">
            <small className="d-flex align-items-center">
              <FaBuilding className="me-1" />
              Company Address
            </small>
          </Card.Header>
          <Card.Body className="py-2">
            <div className="small text-muted">
              <FaMapMarkerAlt className="me-1" />
              {companyAddress.street}, {companyAddress.city}, {companyAddress.state} - {companyAddress.pincode}
            </div>
          </Card.Body>
        </Card>
      )}

      {allowCustomAddress && (
        <Form.Group className="mb-3">
          <Form.Check
            type="radio"
            label="Use company address"
            checked={useCompanyAddress}
            onChange={() => handleUseCompanyAddress(true)}
            disabled={disabled}
            className="mb-2"
          />
          <Form.Check
            type="radio"
            label="Use custom address"
            checked={!useCompanyAddress}
            onChange={() => handleUseCompanyAddress(false)}
            disabled={disabled}
          />
        </Form.Group>
      )}

      {useCompanyAddress ? (
        <Card className="border-success">
          <Card.Body className="py-3">
            <div className="text-success small fw-bold mb-1">Selected Address:</div>
            <div className="small">
              <FaMapMarkerAlt className="me-1" />
              {companyAddress.street}, {companyAddress.city}, {companyAddress.state} - {companyAddress.pincode}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-secondary">
          <Card.Header className="bg-secondary text-white py-2">
            <small>Custom Address</small>
          </Card.Header>
          <Card.Body className="py-3">
            <Form.Control
              as="textarea"
              placeholder="Enter delivery address with multiple lines..."
              value={customAddress}
              onChange={(e) => {
                const value = e.target.value;
                setCustomAddress(value);
                if (onChange) onChange(value); // Send the raw textarea value directly
              }}
              disabled={disabled}
              required={required && !useCompanyAddress}
              rows={4}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </Card.Body>
        </Card>
      )}

      {required && (
        <Form.Text className="text-muted">
          <small>Address is required for this transaction</small>
        </Form.Text>
      )}
    </div>
  );
};

export default CompanyAddressSelect;
